import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  userProfileSchema,
  type UserProfileFormData,
} from "@/lib/user-validations";
import { User, Camera, X } from "lucide-react";
import { formatRole } from "@/lib/utils";
import { logger } from "@/utils/logger";

interface UserProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const UserProfileModal = ({ open, onOpenChange }: UserProfileModalProps) => {
  const { currentUser, updateUser } = useCurrentUser();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // Inicializar selectedRoles
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const availableRoles = [
    "Administrador",
    "Usuário",
    "Editor",
    "Visualizador",
    "Gerente",
    "Analista",
  ];

  // Inicializar formulário
  const form = useForm<UserProfileFormData>({
    resolver: zodResolver(userProfileSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      roles: [],
      company: "",
    },
  });

  // Atualizar formulário quando os dados do usuário mudarem
  useEffect(() => {
    if (open && currentUser) {
      // Atualizar valores do formulário
      form.setValue("name", currentUser.name || "");
      form.setValue("email", currentUser.email || "");
      form.setValue("phone", currentUser.phone || "");
      form.setValue("company", currentUser.company || "");

      // Atualizar roles
      const roles = Array.isArray(currentUser.roles) ? currentUser.roles : [];
      form.setValue("roles", roles);
      setSelectedRoles(roles);

      // Log para depuração
      if (process.env.NODE_ENV === "development") {
        logger.info("🔍 UserProfileModal - currentUser:", currentUser);
      }
    }
  }, [currentUser, open, form]);

  const addRole = (role: string) => {
    if (!selectedRoles.includes(role)) {
      const newRoles = [...selectedRoles, role];
      setSelectedRoles(newRoles);
      form.setValue("roles", newRoles);
    }
  };

  const removeRole = (roleToRemove: string) => {
    const newRoles = selectedRoles.filter((role) => role !== roleToRemove);
    setSelectedRoles(newRoles);
    form.setValue("roles", newRoles);
  };

  const onSubmit = async (data: UserProfileFormData) => {
    setIsLoading(true);
    try {
      // Log para depuração
      if (process.env.NODE_ENV === "development") {
        logger.info("📝 Dados do formulário enviados:", data);
      }

      // Atualizar dados do usuário
      updateUser({
        name: data.name,
        email: data.email,
        phone: data.phone,
        roles: data.roles,
        company: data.company,
      });

      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram salvas com sucesso",
      });

      onOpenChange(false);
    } catch (error) {
      logger.error("❌ Erro ao atualizar perfil:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao salvar as informações",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Meu Perfil</DialogTitle>
          <DialogDescription>
            Atualize suas informações pessoais
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Avatar Section */}
          <div className="flex flex-col items-center space-y-2">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarImage src={currentUser.avatar} />
                <AvatarFallback>
                  <User className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="absolute -bottom-2 -right-2 h-8 w-8 p-0"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-500">Clique para alterar a foto</p>
          </div>

          {/* Form Fields */}
          <div className="grid gap-4">
            <div>
              <Label htmlFor="name">Nome Completo</Label>
              <Controller
                name="name"
                control={form.control}
                render={({ field }) => (
                  <Input id="name" {...field} className="mt-1" />
                )}
              />
              {form.formState.errors.name && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Controller
                name="email"
                control={form.control}
                render={({ field }) => (
                  <Input id="email" type="email" {...field} className="mt-1" />
                )}
              />
              {form.formState.errors.email && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="phone">Telefone</Label>
              <Controller
                name="phone"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="phone"
                    {...field}
                    placeholder="(11) 99999-9999"
                    className="mt-1"
                  />
                )}
              />
            </div>

            <div>
              <Label htmlFor="company">Empresa</Label>
              <Controller
                name="company"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="company"
                    {...field}
                    readOnly
                    className="mt-1 bg-gray-50"
                  />
                )}
              />
            </div>

            <div>
              <Label>Perfis (Multiselect)</Label>
              <div className="mt-1 space-y-2">
                <Select onValueChange={addRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione seus perfis" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles
                      .filter((role) => !selectedRoles.includes(role))
                      .map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                {selectedRoles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedRoles.map((role) => (
                      <Badge
                        key={role}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {formatRole(role)}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={() => removeRole(role)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              {form.formState.errors.roles && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.roles.message}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileModal;
